import {
  isToday,
  isYesterday,
  isTomorrow,
  parse,
  format,
  formatDistanceToNowStrict,
  formatISO,
  parseISO,
  set,
  isValid,
  differenceInHours,
  minutesToSeconds,
  secondsToMilliseconds,
  hoursToMinutes,
  subYears,
  isBefore,
  addHours,
  addYears,
  differenceInDays,
} from 'date-fns';
import _ from 'lodash';
import { Languages } from 'src/types/translations';
import { Duration } from 'src/types/date';
import i18next from 'i18next';
import 'src/i18n';
import 'src/components/time/i18n';
import { getNavigatorLanguage } from 'src/utils/translations';
import { User } from 'src/types/user';
import { ParamsDatesVacationCell } from 'src/types/vacations';
import { ChatStatus } from 'src/types/chats';
import { LOCALE } from 'src/constants';
import { MILISECONDS, MINUTES } from 'src/constants/date';

const { EN, ES, PT } = Languages;

export const formatRelativeDate = date => {
  try {
    if (isToday(date)) return format(date, 'HH:mm');
    if (isYesterday(date)) return i18next.t('GENERAL:YESTERDAY');

    return format(date, 'dd/MM/yy');
  } catch (err) {
    return '';
  }
};

export const getCompletedDate = (date, user: User) => {
  const dateToFormat = new Date(date);
  const correctLocale = takeCorrectLocale(user);
  try {
    return format(dateToFormat, 'EEEE PPP', { locale: correctLocale });
  } catch (err) {
    return '';
  }
};

export const getNextPostScheduledDate = (date, user: User) => {
  const dateToFormat = new Date(date);
  const languageSymbol = user?.language;
  try {
    if (isToday(dateToFormat))
      return i18next.t('GENERAL:TODAY').toLocaleLowerCase();
    if (isTomorrow(dateToFormat))
      return i18next.t('GENERAL:TOMORROW').toLocaleLowerCase();

    if (languageSymbol === EN) {
      return format(dateToFormat, 'MM/dd/yyyy');
    }
    return format(dateToFormat, 'dd/MM/yyyy');
  } catch (err) {
    return '';
  }
};

export const getTimeFormat = date => {
  const dateToFormat = new Date(date);
  try {
    return format(dateToFormat, 'HH:mm a');
  } catch (err) {
    return '';
  }
};

export const getFormatedDate = (date: string | Date, user: User) => {
  const isEnglish = user?.language === EN;
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return format(parsedDate, isEnglish ? 'MM/dd/yyyy' : 'dd/MM/yyyy');
};

export const formatFullDate = (date, user: User) => {
  const dateObj = new Date(date);
  const languageSymbol = user?.language;
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  return languageSymbol === EN
    ? `${month}/${day}/${year}`
    : `${day}/${month}/${year}`;
};

export const formatDateToMonthYear = (date: string, user: User) => {
  const dateObj = new Date(date);
  const languageSymbol = user?.language || 'en';
  const month = dateObj.toLocaleString(languageSymbol, { month: 'short' });
  const year = dateObj.getFullYear();
  return `${month.toUpperCase()} ${year}`;
};

export const getFormatedDateTime = (date, user: User) => {
  const isEnglish = user?.language === EN;
  return format(
    parseISO(date),
    isEnglish ? 'MM/dd/yyyy HH:mm' : 'dd/MM/yyyy HH:mm',
  );
};

export const formatToTime = date => format(date, 'HH:mm');

const adjustForMexicoCentral = date => addHours(date, -6);

export const formatDateTimeMonterrey = (date, user: User) => {
  const isEnglish = user?.language === EN;
  const parsedDate = parseISO(date);
  const userDateTime = adjustForMexicoCentral(parsedDate);
  return format(
    userDateTime,
    isEnglish ? 'MM/dd/yyyy HH:mm' : 'dd/MM/yyyy HH:mm',
  );
};

export const takeCorrectLocale = (user: User) => {
  let languageSymbol = user?.language;
  if (!languageSymbol) {
    languageSymbol = getNavigatorLanguage();
  }

  if (languageSymbol === ES || languageSymbol === EN || languageSymbol === PT) {
    return LOCALE[languageSymbol];
  }
  return null;
};

export const formatDistanceToNow = (date, user: User, onlyDistance = false) => {
  const correctLocale = takeCorrectLocale(user);
  return i18next.t(onlyDistance ? 'TIME:DISTANCE' : 'TIME:TIME_DISTANCE', {
    distance: formatDistanceToNowStrict(date, { locale: correctLocale }),
  });
};

export const formatDateToLongLocalizedDate = (date, user: User) => {
  const dateToFormat = new Date(date);
  const correctLocale = takeCorrectLocale(user);
  try {
    return format(dateToFormat, 'PPP', { locale: correctLocale });
  } catch (err) {
    return '';
  }
};

export const formatDateInChatMessages = (date, user: User) => {
  const dateToFormat = new Date(date);
  const correctLocale = takeCorrectLocale(user);
  try {
    if (isToday(dateToFormat)) return i18next.t('GENERAL:TODAY');
    if (isYesterday(dateToFormat)) return i18next.t('GENERAL:YESTERDAY');

    return format(dateToFormat, 'MMMM d', { locale: correctLocale });
  } catch (err) {
    return '';
  }
};

export const formatUTCLocalDate = (
  date: string | Date,
  user: User,
  pattern = user?.language === EN ? 'MM/dd/yyyy' : 'dd/MM/yyyy',
) => {
  const dateToFormat = new Date(date);
  const locale = takeCorrectLocale(user);
  try {
    return format(dateToFormat, pattern, { locale });
  } catch (err) {
    return '';
  }
};

export const formatUTCDate = (date: any, pattern = 'dd/MM/yyyy'): string =>
  format(new Date(date), pattern);

export const isSameUTCDate = (a, b): boolean =>
  formatUTCDate(a) === formatUTCDate(b);

export const addOneDay = date => new Date(date.setDate(date.getDate() + 1));

export const addRemoveDay = (date: string, add = true) => {
  const d = new Date(date);
  return add ? d.setDate(d.getDate() + 1) : d.setDate(d.getDate() - 1);
};

export const getMonthActual = (user: User) => {
  const dateToFormat = new Date();
  return format(dateToFormat, 'MMMM', { locale: LOCALE[user.language] });
};

export const localDateToSimpleDateString = (date: Date) =>
  formatISO(date, { representation: 'date' });

export const simpleDateStringToLocalDate = (utcISOdate: string | Date) => {
  if (typeof utcISOdate !== 'string') {
    return utcISOdate;
  }
  const dateInUTC = parseISO(utcISOdate);

  const newDate = new Date();
  return set(newDate, {
    year: dateInUTC.getUTCFullYear(),
    month: dateInUTC.getUTCMonth(),
    date: dateInUTC.getUTCDate(),
  });
};

export const orderByDate = (a, b) =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

export const getHiringDate = (date, user) => {
  let formattedDate = '';
  if (date) {
    const dateParsed = parse(date, 'yyyy-MM-dd', new Date());
    if (isValid(dateParsed)) {
      formattedDate = format(dateParsed, 'PP', {
        locale: LOCALE[user.language],
      });
    }
  }
  return formattedDate;
};

export const formatDateInVacations = ({
  date,
  user,
  type,
  isUtc,
}: ParamsDatesVacationCell) => {
  let dateToFormat;
  if (_.isString(date)) {
    if (isUtc) {
      dateToFormat = new Date(date);
    } else {
      dateToFormat = new Date(parseISO(date));
    }
  } else {
    dateToFormat = date;
  }
  const correctLocale = takeCorrectLocale(user);
  try {
    return format(dateToFormat, type, { locale: correctLocale });
  } catch (err) {
    return '';
  }
};

const getDifferenceInHours = (
  dateLeft?: Date,
  dateRight?: Date,
): number | undefined => {
  const difference = dateLeft
    ? differenceInHours(
        new Date(dateLeft),
        dateRight ? new Date(dateRight) : new Date(),
      )
    : undefined;
  return Math.max(difference, 0);
};

export const getResolutionTime = (dueDate: Date, createdAt?: Date) => {
  const hoursToResolve = getDifferenceInHours(dueDate, createdAt);
  if (!hoursToResolve) {
    return '0h';
  }

  const resolutionTimeDays = Math.trunc(hoursToResolve / 24);
  const resolutionTimeHours = Math.trunc(hoursToResolve % 24);

  let formattedResolutionTime = '';
  if (resolutionTimeDays > 0) {
    formattedResolutionTime += `${resolutionTimeDays}d `;
  }
  if (resolutionTimeHours > 0) {
    formattedResolutionTime += `${resolutionTimeHours}h`;
  }

  return formattedResolutionTime;
};

export const getColorByTime = (
  createdAt: Date,
  dueDate: Date,
  tabSelected: ChatStatus,
) => {
  if (tabSelected === ChatStatus.UNASSIGNED) {
    return 'secondary';
  } else if (tabSelected === ChatStatus.CLOSED) {
    return 'primary';
  }

  const hoursTotalTime = getDifferenceInHours(dueDate, createdAt);
  const hoursToResolve = getDifferenceInHours(dueDate);

  if (hoursToResolve > hoursTotalTime * 0.66) {
    return 'success';
  }

  if (hoursToResolve > hoursTotalTime * 0.33) {
    return 'warning';
  }

  return 'error';
};

export const getTotalDuration = (duration: Duration) => {
  const newDuration = {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  };

  newDuration.hours += duration?.hours || 0;
  newDuration.minutes += hoursToMinutes(newDuration.hours);

  newDuration.minutes += duration?.minutes || 0;
  newDuration.seconds += minutesToSeconds(newDuration.minutes);

  newDuration.seconds += duration?.seconds || 0;
  newDuration.milliseconds += secondsToMilliseconds(newDuration.seconds);

  return newDuration;
};

export const formatDateDifference = (lastUpdateDate, t) => {
  if (!lastUpdateDate) return null;

  const now = new Date();
  const nowUTC = now.getTime();

  const lastUpdateUTC = new Date(lastUpdateDate).getTime();

  const diffDate = Math.abs(nowUTC - lastUpdateUTC);

  const minutes = Math.floor(diffDate / MILISECONDS.MINUTE);
  const hours = Math.floor(diffDate / MILISECONDS.HOUR);
  const days = Math.floor(diffDate / MILISECONDS.DAY);

  if (minutes < MINUTES.MINUTE) {
    return t('LESS_THAN_A_MINUTE');
  } else if (minutes < MINUTES.HOUR) {
    return t('MINUTES_AGO', { count: minutes });
  } else if (minutes < MINUTES.DAY) {
    return t('HOURS_AGO', { count: hours });
  }
  return t('DAYS_AGO', { count: days });
};

export const isMoreThanOneYearOld = dateString => {
  if (!dateString) return dateString;
  const oneYearAgo = subYears(new Date(), 1);
  return isBefore(parseISO(dateString), oneYearAgo);
};

export const differenceOneYearDays = dateString => {
  if (!dateString) return dateString;
  const currentDate = new Date();
  const hiringDate = new Date(dateString);
  if (hiringDate > currentDate) {
    return null;
  }
  const aniversary = addYears(hiringDate, 1);
  return differenceInDays(aniversary, currentDate);
};

/**
 * Formats a given date into a localized string representation based on the current language setting.
 *
 * @param {string | Date} date - The date to format, which can be either a string or a Date object.
 * @returns {string | null} - The formatted date string in uppercase or null is the date is invalid.
 *
 * @example
 * Assuming language is 'en' -> 'DEC/10/2024'
 *
 * @example
 * Assuming language is 'es' -> '10/DIC/2024'
 */
export const namedFormatUTCDate = (date: string | Date) => {
  if (!date) return null;
  const localDate = simpleDateStringToLocalDate(date);
  if (localDate.toString() !== 'Invalid Date') {
    const isEs = i18next.language === 'es';
    return format(localDate, isEs ? 'dd/MMM/yyyy' : 'MMM/dd/yyyy', {
      locale: isEs ? LOCALE.es : LOCALE.en,
    }).toUpperCase();
  }
  return null;
};

export const formatTime = (date: Date, pattern = 'HH:mm') => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  if (pattern === 'HH:mm') {
    return `${hours}:${minutes}`;
  }
  return `${hours}:${minutes}`;
};

export const formatDateTime = (date: string | Date) => {
  const localDate = simpleDateStringToLocalDate(date);
  if (localDate.toString() !== 'Invalid Date') {
    const isEs = i18next.language === 'es';
    return format(localDate, isEs ? 'dd/MMM/yyyy HH:mm' : 'MMM/dd/yyyy HH:mm', {
      locale: isEs ? LOCALE.es : LOCALE.en,
    }).toUpperCase();
  }
  return null;
};

export const dateIsBefore = (
  date: Date | string,
  dateToCompare: Date | string,
) => isBefore(new Date(date), new Date(dateToCompare));

export const getBanBajioCurrentDate = () => {
  const currentDate = new Date();
  const isEs = i18next.language === 'es';
  let day;
  if (isEs) {
    day = format(currentDate, 'dd', { locale: LOCALE.es });
  } else {
    day = format(currentDate, 'do', { locale: LOCALE.en });
  }
  const month = format(currentDate, 'MMM', {
    locale: isEs ? LOCALE.es : LOCALE.en,
  }).toUpperCase();
  const year = format(currentDate, 'yyyy', {
    locale: isEs ? LOCALE.es : LOCALE.en,
  });
  return { day, month, year };
};

export const formatDateEvents = (date, user: User) => {
  const dateToFormat = new Date(date);
  const correctLocale = takeCorrectLocale(user);
  try {
    if (isToday(dateToFormat)) return i18next.t('GENERAL:TODAY');
    if (isTomorrow(dateToFormat)) return i18next.t('GENERAL:TOMORROW');

    return format(dateToFormat, 'PPP', { locale: correctLocale });
  } catch (err) {
    return '';
  }
};
